import React from 'react'
import PropTypes from 'prop-types'
import './Icon.css'

/**
 * Icon Component - Wrapper for Streamline SVG icons
 * 
 * This component provides a consistent interface for using Streamline icons throughout the app.
 * As you download icons from Streamline, add them to the imports below and to the iconMap.
 * 
 * Usage:
 *   <Icon name="home" size={20} />
 *   <Icon name="user-circle" size={24} className="custom-class" />
 */

// =============================================================================
// STEP 1: Import your downloaded Streamline SVG icons here
// =============================================================================
// 
// As you download icons from Streamline, import them here using the ?react suffix
// so they can be used as React components.
//
import SingleNeutralActionSettingIcon from '@/assets/icons/streamline/single-neutral-actions-setting.svg?react'
import MoneyWalletIcon from '@/assets/icons/streamline/money-wallet.svg?react'
import ArrowLeftIcon from '@/assets/icons/streamline/keyboard-arrow-left.svg?react'
import ArrowRightIcon from '@/assets/icons/streamline/keyboard-arrow-right.svg?react'
import AppWindowUserIcon from '@/assets/icons/streamline/app-window-user.svg?react'
import LogoutIcon from '@/assets/icons/streamline/logout-1.svg?react'
import AnalyticsBarGraphIcon from '@/assets/icons/streamline/analytics-graph-bar-1.svg?react'
import AddCircleIcon from '@/assets/icons/streamline/add-circle.svg?react'
import CreditCardSyncIcon from '@/assets/icons/streamline/credit-card-sync.svg?react'
import CloseIcon from '@/assets/icons/streamline/close.svg?react'
import ArrowUpIcon from '@/assets/icons/streamline/arrow-up-1.svg?react'
import ArrowDownIcon from '@/assets/icons/streamline/arrow-down-1.svg?react'
import MultipleActionsAddIcon from '@/assets/icons/streamline/multiple-actions-add.svg?react'
import EditIcon from '@/assets/icons/streamline/pencil-write.svg?react'
import SingleNeutralActionsUpload from '@/assets/icons/streamline/single-neutral-actions-upload.svg?react'
import LockIcon from '@/assets/icons/streamline/lock-1.svg?react'
import SingleNeutralCardIdIcon from '@/assets/icons/streamline/single-neutral-id-card-1.svg?react'
import RevealPasswordIcon from '@/assets/icons/streamline/view-1.svg?react'
import HidePasswordIcon from '@/assets/icons/streamline/view-off.svg?react'
import AnalyticsBarsIcon from '@/assets/icons/streamline/analytics-bars.svg?react'
import InfoCircleIcon from '@/assets/icons/streamline/info-circle.svg?react'
import TaxReportIcon from '@/assets/icons/streamline/tax-report.svg?react'

// =============================================================================
// STEP 2: Map icon names to components
// =============================================================================
//
// Add your imported icons to this object with friendly names.
// The key is what you'll use in <Icon name="key" />
//
const iconMap = {
    'user-profile-setting': SingleNeutralActionSettingIcon,
    'money-wallet': MoneyWalletIcon,
    'arrow-left': ArrowLeftIcon,
    'arrow-right': ArrowRightIcon,
    'app-window-user': AppWindowUserIcon,
    'sign-out': LogoutIcon,
    'analytics-bar-graph': AnalyticsBarGraphIcon,
    'add-circle': AddCircleIcon,
    'credit-card-sync': CreditCardSyncIcon,
    'close': CloseIcon,
    'arrow-up': ArrowUpIcon,
    'arrow-down': ArrowDownIcon,
    'multiple-actions-add': MultipleActionsAddIcon,
    'edit': EditIcon,
    'single-neutral-actions-upload': SingleNeutralActionsUpload,
    'lock': LockIcon,
    'single-neutral-id-card': SingleNeutralCardIdIcon,
    'reveal-password': RevealPasswordIcon,
    'hide-password': HidePasswordIcon,
    'analytics-bars': AnalyticsBarsIcon,
    'info-circle': InfoCircleIcon,
    'tax-report': TaxReportIcon,
}

/**
 * Icon Component
 * @param {string} name - The name of the icon (must match a key in iconMap)
 * @param {number} size - The width and height of the icon in pixels (default: 20)
 * @param {string} className - Additional CSS class names
 * @param {object} style - Inline styles to apply to the icon
 * @param {object} props - Additional props passed to the SVG element
 */
export const Icon = ({ name, size = 20, className = '', style = {}, ...props }) => {
    const IconComponent = iconMap[name]

    // If icon not found, show a warning in development
    if (!IconComponent) {
        if (process.env.NODE_ENV === 'development') {
            console.warn(`Icon "${name}" not found in iconMap. Make sure to import it and add it to the iconMap object in Icon.jsx`)
        }
        // Return a placeholder div in development, nothing in production
        return process.env.NODE_ENV === 'development' ? (
            <div
                style={{
                    width: size,
                    height: size,
                    display: 'inline-block',
                    backgroundColor: '#ff000020',
                    border: '1px dashed red',
                    ...style
                }}
                className={className}
                title={`Missing icon: ${name}`}
            />
        ) : null
    }

    return (
        <IconComponent
            width={size}
            height={size}
            className={className}
            style={style}
            {...props}
        />
    )
}

Icon.propTypes = {
    name: PropTypes.string.isRequired,
    size: PropTypes.number,
    className: PropTypes.string,
    style: PropTypes.object,
}

export default Icon